// This client was automatically generated by Segment Typewriter. ** Do Not Edit **
// To update this file, run:
//   npx typewriter

/**
 * Records the page viewed by the visitor and associated properties, used for full page
 * takeover. Trigger when URL changes, not for modals or other surfaces that do not change
 * the URL.
 */
export interface AlertViewed {
    /**
     * Also referred to as Workspace ID
     */
    account_id?: string;
    /**
     * Detailed API, regex response
     */
    alert_message?: string;
    cart_id?:       string;
    /**
     * Custom categorization of pages based on URL such as Homepage, Overview Page, Login Page,
     * MyLZ, etc.
     */
    category?:     string;
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    keywords?:     string;
    package_id?:   string;
    /**
     * page_path in Amplitude
     */
    path?:                string;
    processing_order_id?: string;
    /**
     * page_referrer in Amplitude
     */
    referrer?:    string;
    screen_name?: string;
    /**
     * page_params in Amplitude
     */
    search?: string;
    /**
     * page_title in Amplitude
     */
    title?: string;
    /**
     * Custom grouping of pages referring to the highest order product or package (LLC, Inc,
     * Nonprofit, DBA) or standalone products (RA, Forms)
     */
    topline_product_group?: null | string;
    /**
     * use this to indicate if the alert was displayed, hidden, popup, context change, whatever
     * is useful for understanding what happened where
     */
    track_type?: string;
    url?:        string;
    user_input?: string;
    
}

/**
 * Records the answers viewed by the visitor (e.g. on RYA page at end of Q2 or on SSE
 * self-serve exception page) and associated properties.
 */
export interface AnswersViewed {
    /**
     * Also referred to as Workspace ID
     */
    account_id?:         null | string;
    additional_details?: AnswersViewedAdditionalDetails;
    cart_id?:            string;
    /**
     * Custom categorization of pages based on URL such as Homepage, Overview Page, Login Page,
     * MyLZ, etc.
     */
    category?:    string;
    checkout_id?: string;
    /**
     * Specifically for screens like the activity feed where they want to list all the items
     * present.
     */
    details_list?:        any[];
    entitlements?:        any[];
    graph_type?:          string;
    hash?:                string;
    html_element_id?:     string;
    order_id?:            string;
    package_id?:          string;
    page_section?:        string;
    process_id?:          string;
    processing_order_id?: string;
    products?:            AnswersViewedProduct[];
    /**
     * Friendly name for easy sorting and filtering
     */
    screen_name?: string;
    /**
     * modal, drawer, partial page takeover
     */
    screen_type?:  string;
    surface_name?: string;
    /**
     * Custom grouping of pages referring to the highest order product or package (LLC, Inc,
     * Nonprofit, DBA) or standalone products (RA, Forms)
     */
    topline_product_group?: null | string;
    type?:                  string;
    
}

export interface AnswersViewedAdditionalDetails {
    confirm_modal_heading?: string;
    doc_type?:              string;
    document?:              string;
    experiment?:            string;
    experiment_value?:      string;
    industry?:              string;
    partner?:               string;
    report_title?:          string;
    selected?:              string;
    status?:                string;
    surface?:               string;
    takeover?:              string;
    task_name?:             string;
    
}

export interface AnswersViewedProduct {
    ltv?:                   number;
    name?:                  string;
    order_id?:              string;
    order_item_id?:         string;
    order_location?:        string;
    paid_amount?:           number;
    price?:                 number;
    process?:               string;
    process_id?:            string;
    processing_order_id?:   string;
    product_group?:         string;
    product_id?:            string;
    product_line?:          string;
    quantity?:              number;
    sku?:                   string;
    topline_product_group?: string;
    
}

/**
 * Records the page viewed by the visitor and associated properties, used for full page
 * takeover. Trigger when URL changes, not for modals or other surfaces that do not change
 * the URL.
 */
export interface AppScreenViewed {
    /**
     * Also referred to as Workspace ID
     */
    account_id?: string;
    /**
     * Custom categorization of pages based on URL such as Homepage, Overview Page, Login Page,
     * MyLZ, etc.
     */
    category?:     string;
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    keywords?:     string;
    /**
     * page_path in Amplitude
     */
    path?: string;
    /**
     * page_referrer in Amplitude
     */
    referrer?: string;
    /**
     * page_params in Amplitude
     */
    search?: string;
    /**
     * page_title in Amplitude
     */
    title?: string;
    /**
     * Custom grouping of pages referring to the highest order product or package (LLC, Inc,
     * Nonprofit, DBA) or standalone products (RA, Forms)
     */
    topline_product_group?: string;
    url?:                   string;
    
}

/**
 * iOS App specific event, created by the Segment iOS SDK
 */
export interface ApplicationBackgrounded {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    _?: null;
    
}

/**
 * iOS App specific event, created by the Segment iOS SDK
 */
export interface ApplicationForegrounded {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    _?: null;
    
}

/**
 * iOS App specific event, created by the Segment iOS SDK
 */
export interface ApplicationInstalled {
    build?:   string;
    version?: string;
    
}

/**
 * iOS App specific event, created by the Segment iOS SDK
 */
export interface ApplicationOpened {
    build?:                 string;
    from_background?:       boolean;
    referring_application?: string;
    url?:                   string;
    version?:               string;
    
}

/**
 * iOS App specific event, created by the Segment iOS SDK
 */
export interface ApplicationUpdated {
    build?:            string;
    previous_build?:   string;
    previous_version?: string;
    version?:          string;
    
}

/**
 * Manually Instrumented server-side event to capture items that are created asynchronously
 * that a user will be able to later interact with if they view the content.
 */
export interface AsyncContentCreated {
    /**
     * Also referred to as Workspace ID
     */
    account_id?: string;
    /**
     * friendly name of what the content is. Examples: For You: Connect Bank Account
     */
    async_content_name?: string;
    /**
     * task creation, etc.
     */
    async_content_type: string;
    /**
     * Custom categorization of pages based on URL such as Homepage, Overview Page, Login Page,
     * MyLZ, etc.
     */
    category?: string;
    /**
     * To capture the full copy of the Content Viewed
     */
    copy?: string;
    /**
     * Flat object to capture additional details
     */
    details?:         AsyncContentCreatedDetails;
    entitlements?:    any[];
    graph_type?:      string;
    hash?:            string;
    html_element_id?: string;
    package_id?:      string;
    page_section?:    string;
    /**
     * Friendly name for easy sorting and filtering
     */
    screen_name?: string;
    /**
     * modal, drawer, banner, partial page takeover, full page, etc
     */
    screen_type?:  string;
    surface_name?: string;
    /**
     * Custom grouping of pages referring to the highest order product or package (LLC, Inc,
     * Nonprofit, DBA) or standalone products (RA, Forms)
     */
    topline_product_group?: null | string;
    version_identifier?:    string;
    
}

/**
 * Flat object to capture additional details
 */
export interface AsyncContentCreatedDetails {
    doc_count?:    string;
    entitled?:     string;
    id?:           string;
    included?:     string;
    name?:         string;
    not_included?: string;
    order_status?: string;
    package_id?:   string;
    package_name?: string;
    plan?:         string;
    status?:       string;
    subtitle?:     string;
    title?:        string;
    variant?:      string;
    
}

/**
 * At the very first moment a purchase flow has begun (entering the IQ flow for example).
 */
export interface CartCreated {
    account_id?: null | string;
    cart_id?:    string;
    /**
     * Where in the LZ ecosystem, Article Center, MyLZ, Overview Page, Q1 flow, etc.
     */
    category?: string;
    cta_copy?: string;
    /**
     * Array of entitlements
     */
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    /**
     * Friendly name of the item being tracked format <ui component> | <event type> | <relevant
     * text to identify the component>
     */
    interaction_name?: string;
    /**
     * Used when the interaction is a multi-choice selector on a single question. Examples in
     * GTKY flow.
     */
    option_selected?: string;
    /**
     * The site or application where the order originated
     */
    order_location: CartCreatedOrderLocation;
    package_id?:    string;
    /**
     * Used when the same CTA button is used on several different parts of a single page, but at
     * different sections.
     */
    page_section?:        string;
    process_id?:          string;
    process_name?:        string;
    processing_order_id?: string;
    product_id?:          string;
    products?:            CartCreatedProduct[];
    /**
     * If the track occurs on a screen identify that here.
     */
    screen_name?:           string;
    sku?:                   string;
    target?:                string;
    topline_product_group?: string;
    /**
     * link, button, scroll, hover, input, focus
     */
    track_type?: string;
    type?:       string;
    user_input?: string;
    
}

/**
 * The site or application where the order originated
 */
export type CartCreatedOrderLocation = "Crossell" | "Growth" | "LZWeb" | "MyLZ" | "Q1" | "Q2";

export interface CartCreatedProduct {
    name?:       string;
    package_id?: string;
    
}

/**
 * RYO Page, or other pages/surfaces that show the full contents of the cart and payments
 * required.
 */
export interface CartViewed {
    account_id?:   null | string;
    cart_id:       string;
    category:      string;
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    /**
     * The site or application where the order originated
     */
    order_location:         CartCreatedOrderLocation;
    package_id?:            string;
    package_name?:          string;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    products:               CartViewedProduct[];
    screen_name?:           string;
    topline_product_group?: string;
    total_cart_value?:      number;
    user_input?:            string;
    
}

export interface CartViewedProduct {
    category?:              string;
    currency?:              string;
    name?:                  string;
    package_id?:            string;
    payment_plan_unit?:     string;
    position?:              number;
    price?:                 number;
    product_id?:            string;
    sku?:                   string;
    topline_product_group?: string;
    url?:                   string;
    
}

export interface CheckoutCompleted {
    account_id?:            null | string;
    cart_id?:               string;
    category?:              string;
    checkout_id?:           string;
    cta_copy?:              string;
    entitled?:              string;
    entitlements?:          any[];
    graph_type?:            string;
    hash?:                  string;
    interaction_name?:      string;
    option_selected?:       string;
    package_id?:            string;
    page_section?:          string;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    screen_name?:           string;
    target?:                string;
    topline_product_group?: string;
    track_type?:            string;
    
}

/**
 * When a checkout process is initiated. Example, when the "Continue to Checkout" button is
 * clicked on RYO in the Q1 flow.
 */
export interface CheckoutStarted {
    account_id?:   null | string;
    affiliation?:  string;
    cart_id?:      string;
    category?:     string;
    coupon?:       string;
    currency?:     string;
    discount?:     string;
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    order_id?:     string;
    /**
     * The site or application where the order originated
     */
    order_location?:        CartCreatedOrderLocation | null;
    package_id?:            string;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    products?:              CheckoutStartedProduct[];
    revenue?:               string;
    screen_name?:           string;
    shipping?:              string;
    tax?:                   string;
    topline_product_group?: string;
    value?:                 string;
    
}

export interface CheckoutStartedProduct {
    category?:              string;
    currency?:              string;
    name?:                  string;
    package_id?:            string;
    position?:              number;
    price?:                 string;
    product_id?:            string;
    sku?:                   string;
    topline_product_group?: string;
    url?:                   string;
    
}

export interface CheckoutStepCompleted {
    account_id?:            null | string;
    cart_id?:               string;
    category?:              string;
    checkout_id?:           string;
    cta_copy?:              string;
    entitled?:              string;
    entitlements?:          any[];
    graph_type?:            string;
    hash?:                  string;
    interaction_name?:      string;
    option_selected?:       string;
    package_id?:            string;
    page_section?:          string;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    screen_name?:           string;
    target?:                string;
    topline_product_group?: string;
    track_type?:            string;
    
}

/**
 * Triggers only during checkout
 */
export interface CheckoutStepViewed {
    account_id?: null | string;
    cart_id?:    string;
    /**
     * Where in the LZ ecosystem, Article Center, MyLZ, Overview Page, Q1 flow, etc.
     */
    category?: string;
    cta_copy?: string;
    /**
     * Array of entitlements
     */
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    /**
     * Friendly name of the item being tracked format <ui component> | <event type> | <relevant
     * text to identify the component>
     */
    interaction_name?: string;
    /**
     * Used when the interaction is a multi-choice selector on a single question. Examples in
     * GTKY flow.
     */
    option_selected?: string;
    /**
     * The site or application where the order originated
     */
    order_location?: CartCreatedOrderLocation | null;
    package_id?:     string;
    /**
     * Used when the same CTA button is used on several different parts of a single page, but at
     * different sections.
     */
    page_section?:        string;
    process_id?:          string;
    process_name?:        string;
    processing_order_id?: string;
    product_id?:          string;
    /**
     * If the track occurs on a screen identify that here.
     */
    screen_name?:           string;
    sku?:                   string;
    target?:                string;
    topline_product_group?: string;
    /**
     * link, button, scroll, hover, input, focus
     */
    track_type?: string;
    type?:       string;
    user_input?: string;
    
}

/**
 * Manually Instrumented event to capture items that are not recorded with Page Viewed,
 * Surface Viewed, or Interactions.
 */
export interface ContentViewed {
    /**
     * Also referred to as Workspace ID
     */
    account_id?: string;
    /**
     * Custom categorization of pages based on URL such as Homepage, Overview Page, Login Page,
     * MyLZ, etc.
     */
    category?: string;
    /**
     * friendly name of what the content is. Examples: For You: Connect Bank Account
     */
    content_name?: string;
    /**
     * for you, banner, notification, status, etc
     */
    content_type?: string;
    /**
     * To capture the full copy of the Content Viewed
     */
    copy?:     string;
    cta_copy?: string;
    /**
     * Flat object to capture additional details
     */
    details?:         ContentViewedDetails;
    entitlements?:    any[];
    graph_type?:      string;
    hash?:            string;
    html_element_id?: string;
    package_id?:      string;
    page_section?:    string;
    /**
     * Friendly name for easy sorting and filtering
     */
    screen_name?: string;
    /**
     * modal, drawer, banner, partial page takeover, full page, etc
     */
    screen_type?:  string;
    surface_name?: string;
    /**
     * Custom grouping of pages referring to the highest order product or package (LLC, Inc,
     * Nonprofit, DBA) or standalone products (RA, Forms)
     */
    topline_product_group?: null | string;
    version_identifier?:    string;
    
}

/**
 * Flat object to capture additional details
 */
export interface ContentViewedDetails {
    card_count?:      string;
    cta_count?:       string;
    current_step?:    string;
    doc_count?:       string;
    entitled?:        string;
    expiration_date?: string;
    has_logo?:        string;
    id?:              string;
    included?:        string;
    is_dismissible?:  string;
    /**
     * The name of the model in Model Hub.
     */
    model_name?: string;
    /**
     * The response of the model in Model Hub
     */
    model_response?: string;
    /**
     * The reason for the response from the model in Model Hub (for example default, fallback,
     * or true model response).
     */
    model_response_reason?: string;
    name?:                  string;
    not_included?:          string;
    number_completed?:      string;
    /**
     * To capture the list of offers presented on the page (eg interstitial-essentials).
     */
    offers?:       string;
    order_status?: string;
    package_id?:   string;
    package_name?: string;
    plan?:         string;
    price?:        string;
    product_id?:   string;
    renewal_date?: string;
    start_date?:   string;
    status?:       string;
    subtitle?:     string;
    title?:        string;
    variant?:      string;
    
}

export interface CustomerInputValidation {
    answer_store?:   AnswerStore[] | null;
    error_messages?: Array<null | ErrorMessage> | null;
    is_LME?:         boolean;
    order_id?:       string;
    process_id?:     string;
    
}

export interface AnswerStore {
    key?:   string;
    value?: string;
    
}

export interface ErrorMessage {
    property?:       string;
    reason_code?:    string;
    reason_message?: string;
    
}

/**
 * Enrolled in an experiment by outside product (Builder.io, Launch Darkly, previously
 * Optimizely)
 */
export interface ExperimentEnrolled {
    account_id?: string;
    /**
     * Set of key:values for additional details
     */
    additional_details?:     null | ExperimentEnrolledAdditionalDetails;
    builder_experiment_id?:  string;
    builder_id?:             string;
    builder_variation_id?:   string;
    cart_id?:                string;
    category?:               string;
    entitlements?:           any[];
    event_type?:             string;
    experiment_id?:          number;
    experiment_name?:        string;
    ga_id?:                  null;
    ga_order_id?:            null;
    graph_type?:             string;
    hash?:                   string;
    lz_cid?:                 string;
    optimizely_end_user_id?: string;
    package_id?:             string;
    page_path?:              string;
    page_title?:             string;
    page_url?:               string;
    processing_order_id?:    number;
    referrer?:               string;
    screen_name?:            string;
    session_id?:             string;
    timestamp?:              string;
    topline_product_group?:  string;
    user_agent?:             string;
    variation_id?:           number;
    variation_name?:         string;
    
}

export interface ExperimentEnrolledAdditionalDetails {
    industry?:       null | string;
    license_number?: null | string;
    
}

export interface ExperimentViewed {
    account_id?: string;
    /**
     * Set of key:values for additional details
     */
    additional_details?:    null | ExperimentViewedAdditionalDetails;
    builder_id?:            string;
    category?:              string;
    cell?:                  number;
    entitlements?:          any[];
    experiment_id:          string;
    experiment_name?:       string;
    graph_type?:            string;
    hash?:                  string;
    lz_cid?:                string;
    package_id?:            string;
    screen_name?:           string;
    topline_product_group?: string;
    variation_id:           string;
    variation_name?:        string;
    
}

export interface ExperimentViewedAdditionalDetails {
    industry?:       null | string;
    license_number?: null | string;
    
}

/**
 * Web based user request for more information or consultation
 */
export interface FormDetails {
    account_id?:         string;
    additional_details?: FormDetailsAdditionalDetails;
    category?:           string;
    customer_name?:      string;
    email?:              string;
    entitlements?:       any[];
    form_name?:          string;
    graph_type?:         string;
    hash?:               string;
    note?:               any;
    page_path?:          string;
    phone_number?:       string;
    state?:              string;
    url?:                string;
    zip_code?:           string;
    
}

export interface FormDetailsAdditionalDetails {
    marital_status?:           string;
    rental_property?:          string;
    subscribed_legal_service?: string;
    
}

export interface FulfillmentCompleted {
    fulfillment_status?:           string;
    is_fraud_detected?:            boolean;
    is_payment_received?:          boolean;
    is_questionnaire_completed?:   boolean;
    is_terms_of_service_accepted?: boolean;
    order_id?:                     string;
    products?:                     FulfillmentCompletedProduct[];
    rejection_risk_bypass?:        FulfillmentCompletedRejectionRiskBypass[];
    
}

export interface FulfillmentCompletedProduct {
    order_item_id?: string;
    /**
     * formation state, dba county, or federal for EIN
     */
    processing_jurisdiction?: string;
    processing_order_id?:     string;
    /**
     * Product Configuration ID
     */
    product_id?: string;
    /**
     * formation fulfillment, ein fulfillment, initial report fulfillment, etc
     */
    sub_process?: string;
    
}

export interface FulfillmentCompletedRejectionRiskBypass {
    field?: string;
    
}

export interface FulfillmentStarted {
    fulfillment_status?:           string;
    is_fraud_detected?:            boolean;
    is_payment_received?:          boolean;
    is_questionnaire_completed?:   boolean;
    is_terms_of_service_accepted?: boolean;
    order_id?:                     string;
    products?:                     FulfillmentStartedProduct[];
    rejection_risk_bypass?:        FulfillmentStartedRejectionRiskBypass[];
    
}

export interface FulfillmentStartedProduct {
    order_item_id?: string;
    /**
     * formation state, dba county, or federal for EIN
     */
    processing_jurisdiction?: string;
    processing_order_id?:     string;
    /**
     * Product Configuration ID
     */
    product_id?: string;
    /**
     * formation fulfillment, ein fulfillment, initial report fulfillment, etc
     */
    sub_process?: string;
    
}

export interface FulfillmentStartedRejectionRiskBypass {
    field?: string;
    
}

export interface FulfillmentStatusUpdated {
    fulfillment_status:            string;
    is_fraud_detected?:            boolean;
    is_payment_received?:          boolean;
    is_questionnaire_completed?:   boolean;
    is_terms_of_service_accepted?: boolean;
    order_id?:                     string;
    products?:                     FulfillmentStatusUpdatedProduct[];
    rejection_risk_bypass?:        FulfillmentStatusUpdatedRejectionRiskBypass[];
    
}

export interface FulfillmentStatusUpdatedProduct {
    order_item_id?: string;
    /**
     * formation state, dba county, or federal for EIN
     */
    processing_jurisdiction?: string;
    processing_order_id?:     string;
    /**
     * Product Configuration ID
     */
    product_id?: string;
    /**
     * formation fulfillment, ein fulfillment, initial report fulfillment, etc
     */
    sub_process?: string;
    
}

export interface FulfillmentStatusUpdatedRejectionRiskBypass {
    field?: string;
    
}

/**
 * Autotracked Event. Track events. Any engagement with the UI that we want to record.
 * Always include any clicks
 */
export interface Interaction {
    account_id?: null | string;
    /**
     * Set of key:values for additional details wanted/needed on the Interaction
     */
    additional_details?: null | InteractionAdditionalDetails;
    cart_id?:            string;
    /**
     * Where in the LZ ecosystem, Article Center, MyLZ, Overview Page, Q1 flow, iOS App, etc.
     */
    category?: string;
    cta_copy?: string;
    /**
     * Array of entitlements
     */
    entitlements?:    any[];
    graph_type?:      string;
    hash?:            string;
    html_element_id?: string;
    /**
     * Friendly name of the item being tracked format <ui component> | <event type> | <relevant
     * text to identify the component>
     */
    interaction_name?: string;
    /**
     * Used when the interaction allows for input, such as email, multi-choice selector, etc.
     */
    option_selected?: string;
    order_id?:        string;
    package_id?:      string;
    /**
     * Used when the same CTA button is used on several different parts of a single page, but at
     * different sections.
     */
    page_section?:        string;
    process_id?:          string;
    process_name?:        string;
    processing_order_id?: string;
    /**
     * If an interaction is specific to a product, include the Configuration id here. Examples
     * include: Cancellation Flow
     */
    product_id?: string;
    /**
     * If the track occurs on a screen identify that here.
     */
    screen_name?: string;
    /**
     * if the interaction happens related to a specific subscription add the subscription_id
     * here.
     */
    subscription_id?: string;
    /**
     * match the friendly name given to a surface to align interactions to those surfaces
     */
    surface_name?:          string;
    target?:                string;
    topline_product_group?: null | string;
    /**
     * link, button, scroll, hover, input, focus
     */
    track_type?: string;
    user_input?: string;
    /**
     * Account id specific for Virtual Mail service.
     */
    virtual_mail_account_id?: number | null;
    /**
     * Identifier specific for Virtual Mail service.
     */
    virtual_mail_inbox_id?: number | null;
    /**
     * Determines whether the event in the Virtual Mail application was triggered by an
     * impersonating user.
     */
    virtual_mail_is_impersonated?: boolean | null;
    
}

export interface InteractionAdditionalDetails {
    agency?:                   string;
    annual_tax_filing_step?:   string;
    card_title?:               string;
    chip_title?:               string;
    client?:                   string;
    confirm_modal_heading?:    string;
    count_of_signers?:         string;
    customer_id?:              string;
    doc_name?:                 string;
    doc_type?:                 string;
    document?:                 string;
    document_status?:          string;
    document_type?:            string;
    effective_date?:           string;
    entitled?:                 string;
    eSign_subscription_type?:  string;
    experiment?:               string;
    experiment_value?:         string;
    expiration_date?:          string;
    file_path?:                string;
    icon_button?:              string;
    id?:                       string;
    industry?:                 string;
    insight_card_title?:       string;
    jurisdiction_name?:        string;
    jurisdiction_type?:        string;
    label?:                    string;
    license_number?:           string;
    name?:                     string;
    no_expiration?:            string;
    notes?:                    string;
    partner?:                  string;
    payment_profile_id?:       string;
    plan_selected?:            string;
    product?:                  string;
    product_configuration_id?: string;
    proposal?:                 string;
    referrer_url?:             string;
    report_title?:             string;
    return?:                   string;
    root_folder?:              string;
    screen?:                   string;
    selected?:                 string;
    should_create_workorder?:  string;
    signature_flow?:           string;
    source?:                   string;
    status?:                   string;
    surface?:                  string;
    takeover?:                 string;
    task_name?:                string;
    template_name?:            string;
    tile?:                     string;
    zero_state_title?:         string;
    
}

/**
 * Web based user request for more information or consultation
 */
export interface LeadGen {
    account_id?:         string;
    additional_details?: LeadGenAdditionalDetails;
    category?:           string;
    customer_name?:      string;
    email?:              string;
    entitlements?:       any[];
    form_name?:          string;
    graph_type?:         string;
    hash?:               string;
    note?:               any;
    page_path?:          string;
    phone?:              string;
    state?:              string;
    url?:                string;
    zip_code?:           string;
    
}

export interface LeadGenAdditionalDetails {
    /**
     * calendly widget details
     */
    calendly?: PurpleCalendly;
    
}

/**
 * calendly widget details
 */
export interface PurpleCalendly {
    /**
     * unique event uri containing event_uuid
     */
    event_uri?: string;
    /**
     * unique calendly url for the widget
     */
    form_url?: string;
    /**
     * unique invitee uri containing the event_uuid & invitee_uuid
     */
    invitee_uri?: string;
    
}

/**
 * Occurs at the moment a response occurs from the orders api (regardless of payment
 * success).
 */
export interface OrderCompleted {
    account_id?:         null | string;
    affiliation?:        string;
    cart_id?:            string;
    category?:           string;
    checkout_id?:        string;
    cogs?:               number;
    conversion_id?:      string;
    coupon?:             string;
    cta_copy?:           string;
    currency?:           string;
    discount_amount?:    string;
    discount_name?:      string;
    entitled?:           string;
    entitlements?:       any[];
    graph_type?:         string;
    hash?:               string;
    installment_amount?: number;
    installment_fee?:    number;
    interaction_name?:   string;
    intercepted_by?:     string;
    ltv?:                number;
    ltv_net?:            number;
    option_selected?:    string;
    order_id?:           string;
    /**
     * The site or application where the order originated
     */
    order_location?:        CartCreatedOrderLocation;
    order_system?:          OrderCompletedOrderSystem;
    package_id?:            string;
    page_section?:          string;
    parent_order_id?:       string;
    payment_type?:          string;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    products?:              OrderCompletedProduct[];
    revenue?:               number;
    screen_name?:           string;
    shipping?:              number;
    subtotal?:              number;
    target?:                string;
    tax?:                   string;
    topline_product_group?: string;
    total?:                 number;
    track_type?:            string;
    user_input?:            string;
    
}

export type OrderCompletedOrderSystem = "CP2" | "LZ Core" | "Virtual Mail";

export interface OrderCompletedProduct {
    cart_id?:       string;
    category?:      string;
    cogs?:          number;
    graph_type?:    string;
    is_bundle?:     string;
    ltv?:           number;
    name?:          string;
    order_id?:      string;
    order_item_id?: string;
    /**
     * The site or application where the order originated
     */
    order_location?:        ProductOrderLocation;
    package_id?:            string;
    paid_amount?:           string;
    price?:                 number;
    process?:               string;
    process_id?:            string;
    processing_order_id?:   string;
    product_group?:         string;
    product_id?:            string;
    product_line?:          string;
    quantity?:              number;
    sku?:                   string;
    topline_product_group?: string;
    url?:                   string;
    
}

/**
 * The site or application where the order originated
 */
export type ProductOrderLocation = "Crossell" | "LZWeb" | "MyLZ" | "Q1" | "Q2";

/**
 * When a new product is added to an order with the same order_id it is an Order Updated. It
 * should have all the products in the original Order Completed if they have not been
 * cancelled and any additions or changes made to the contents.
 */
export interface OrderUpdated {
    account_id?:         null | string;
    affiliation?:        string;
    cart_id?:            string;
    category?:           string;
    checkout_id?:        string;
    coupon?:             string;
    cta_copy?:           string;
    currency?:           string;
    discount_amount?:    string;
    discount_name?:      string;
    entitled?:           string;
    entitlements?:       any[];
    graph_type?:         string;
    hash?:               string;
    installment_amount?: number;
    installment_fee?:    number;
    interaction_name?:   string;
    ltv?:                number;
    option_selected?:    string;
    order_id?:           string;
    /**
     * The site or application where the order originated
     */
    order_location?:        CartCreatedOrderLocation;
    order_system?:          OrderUpdatedOrderSystem;
    package_id?:            string;
    page_section?:          string;
    payment_type?:          string;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    products?:              OrderUpdatedProduct[];
    revenue?:               number;
    screen_name?:           string;
    shipping?:              number;
    subtotal?:              number;
    target?:                string;
    tax?:                   string;
    topline_product_group?: string;
    total?:                 number;
    track_type?:            string;
    user_input?:            string;
    
}

export type OrderUpdatedOrderSystem = "LZ Core" | "Virtual Mail";

export interface OrderUpdatedProduct {
    cart_id?:       number;
    category?:      string;
    graph_type?:    string;
    ltv?:           string;
    name?:          string;
    order_id?:      string;
    order_item_id?: string;
    /**
     * The site or application where the order originated
     */
    order_location?:        ProductOrderLocation;
    package_id?:            string;
    paid_amount?:           string;
    price?:                 number;
    processing_order_id?:   string;
    product_id?:            string;
    quantity?:              number;
    sku?:                   string;
    topline_product_group?: string;
    url?:                   string;
    
}

/**
 * Autotracked Event. Records the page viewed by the visitor and associated properties, used
 * for full page takeover. Trigger when URL changes, not for modals or other surfaces that
 * do not change the URL.
 */
export interface PageViewed {
    /**
     * Also referred to as Workspace ID
     */
    account_id?: string;
    cart_id?:    string;
    /**
     * Custom categorization of pages based on URL such as Homepage, Overview Page, Login Page,
     * MyLZ, etc.
     */
    category?:     string;
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    keywords?:     string;
    package_id?:   string;
    /**
     * Identifies the partner for partner cobranded landers
     */
    partner?: string;
    /**
     * Used to identify partner-eligible rev-share
     */
    partner_expiration?: string;
    /**
     * page_path in Amplitude
     */
    path?:                string;
    processing_order_id?: string;
    /**
     * page_referrer in Amplitude
     */
    referrer?:    string;
    screen_name?: string;
    /**
     * page_params in Amplitude
     */
    search?:                              string;
    source?:                              string;
    time_spent_on_previous_page_seconds?: number | null;
    /**
     * page_title in Amplitude
     */
    title?: string;
    topic?: string;
    /**
     * Custom grouping of pages referring to the highest order product or package (LLC, Inc,
     * Nonprofit, DBA) or standalone products (RA, Forms)
     */
    topline_product_group?: null | string;
    url?:                   string;
    /**
     * Account id specific for Virtual Mail service.
     */
    virtual_mail_account_id?: number | null;
    /**
     * Identifier specific for Virtual Mail service.
     */
    virtual_mail_inbox_id?: number | null;
    /**
     * Determines whether the event in the Virtual Mail application was triggered by an
     * impersonating user.
     */
    virtual_mail_is_impersonated?: boolean | null;
    
}

export interface PaymentInfoEntered {
    account_id?:   null | string;
    cart_id?:      string;
    category?:     string;
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    order_id?:     string;
    /**
     * The site or application where the order originated
     */
    order_location?: CartCreatedOrderLocation;
    package_id?:     string;
    payment_method?: string;
    /**
     * One Time, 3-pay, etc
     */
    payment_type?:          string;
    process_id?:            string;
    process_name?:          string;
    screen_name?:           string;
    step?:                  number;
    topline_product_group?: string;
    user_input?:            string;
    
}

export interface ProductAdded {
    account_id?:   null | string;
    brand?:        string;
    cart_id?:      string;
    category?:     string;
    coupon?:       string;
    currency?:     string;
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    name?:         string;
    /**
     * The site or application where the order originated
     */
    order_location?:        CartCreatedOrderLocation;
    package_id?:            string;
    payment_plan_unit?:     string;
    position?:              number;
    price?:                 number;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    product_id?:            string;
    quantity?:              number;
    screen_name?:           string;
    sku?:                   string;
    topline_product_group?: string;
    /**
     * Package, Cross-sell, Add-on, etc.
     */
    type?: string;
    url?:  string;
    
}

export interface ProductClicked {
    account_id?:            null | string;
    cart_id?:               string;
    category?:              string;
    cta_copy?:              string;
    entitled?:              string;
    entitlements?:          any[];
    graph_type?:            string;
    hash?:                  string;
    interaction_name?:      string;
    package_id?:            string;
    page_section?:          string;
    price?:                 string;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    product_group?:         string;
    product_id?:            string;
    product_line?:          string;
    screen_name?:           string;
    service_line?:          string;
    sku?:                   string;
    target?:                string;
    topline_product_group?: string;
    track_type?:            string;
    
}

export interface ProductListViewed {
    account_id?:   null | string;
    cart_id?:      string;
    category:      string;
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    list_id?:      string;
    /**
     * The site or application where the order originated
     */
    order_location?:        CartCreatedOrderLocation;
    package_id?:            string;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    products:               ProductListViewedProduct[];
    screen_name?:           string;
    target?:                string;
    topline_product_group?: string;
    
}

export interface ProductListViewedProduct {
    category?:                      string;
    currency?:                      string;
    description?:                   string;
    initiial_subscription_in_days?: string;
    name?:                          string;
    package_id?:                    string;
    position?:                      number;
    price?:                         number;
    processing_order_id?:           string;
    product_id?:                    string;
    sku?:                           string;
    topline_product_group?:         string;
    url?:                           string;
    
}

export interface ProductRemoved {
    account_id?:   null | string;
    brand?:        string;
    cart_id?:      string;
    category?:     string;
    coupon?:       string;
    currency?:     string;
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    name?:         string;
    /**
     * The site or application where the order originated
     */
    order_location?:        CartCreatedOrderLocation;
    package_id?:            string;
    payment_plan_unit?:     string;
    position?:              number;
    price?:                 number;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    product_id?:            string;
    quantity?:              number;
    screen_name?:           string;
    sku?:                   string;
    topline_product_group?: string;
    /**
     * Package, Cross-sell, Add-on, etc.
     */
    type?: string;
    url?:  string;
    
}

export interface PromotionClicked {
    account_id?:       null | string;
    cart_id?:          string;
    category?:         string;
    creative?:         string;
    cta_copy?:         string;
    entitled?:         any[];
    entitlements?:     any[];
    graph_type?:       string;
    hash?:             string;
    interaction_name?: string;
    name?:             string;
    option_selected?:  string;
    /**
     * The site or application where the order originated
     */
    order_location?:        CartCreatedOrderLocation;
    package_id?:            string;
    page_section?:          string;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    product_group?:         string;
    product_id?:            string;
    screen_name?:           string;
    service_line?:          string;
    target?:                string;
    topline_product_group?: string;
    track_type?:            string;
    user_input?:            string;
    
}

export interface PromotionViewed {
    account_id?:       null | string;
    cart_id?:          string;
    category?:         string;
    creative?:         string;
    cta_copy?:         string;
    entitled?:         string;
    entitlements?:     any[];
    graph_type?:       string;
    hash?:             string;
    interaction_name?: string;
    name?:             string;
    option_selected?:  string;
    /**
     * The site or application where the order originated
     */
    order_location?:        CartCreatedOrderLocation;
    package_id?:            string;
    page_section?:          string;
    page_sections?:         string;
    position?:              number;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    screen_name?:           string;
    target?:                string;
    topline_product_group?: string;
    track_type?:            string;
    user_input?:            string;
    
}

export interface PurchaseStepViewed {
    account_id?: null | string;
    cart_id?:    string;
    /**
     * Where in the LZ ecosystem, Article Center, MyLZ, Overview Page, Q1 flow, etc.
     */
    category?: string;
    cta_copy?: string;
    entitled?: string;
    /**
     * Array of entitlements
     */
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    /**
     * Friendly name of the item being tracked format <ui component> | <event type> | <relevant
     * text to identify the component>
     */
    interaction_name?: string;
    /**
     * Used when the interaction is a multi-choice selector on a single question. Examples in
     * GTKY flow.
     */
    option_selected?: string;
    /**
     * The site or application where the order originated
     */
    order_location?: CartCreatedOrderLocation;
    package_id?:     string;
    /**
     * Used when the same CTA button is used on several different parts of a single page, but at
     * different sections.
     */
    page_section?:        string;
    process_id?:          string;
    process_name?:        string;
    processing_order_id?: string;
    product_id?:          number;
    /**
     * If the track occurs on a screen identify that here.
     */
    screen_name?:           string;
    sku?:                   number;
    target?:                string;
    topline_product_group?: string;
    /**
     * link, button, scroll, hover, input, focus
     */
    track_type?: string;
    type?:       string;
    user_input?: string;
    
}

/**
 * At the very first moment a purchase flow has begun where a cart is not created.
 */
export interface QStart {
    account_id?: null | string;
    cart_id?:    string;
    /**
     * Where in the LZ ecosystem, Article Center, MyLZ, Overview Page, Q1 flow, etc.
     */
    category?: string;
    cta_copy?: string;
    /**
     * Array of entitlements
     */
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    /**
     * Friendly name of the item being tracked format <ui component> | <event type> | <relevant
     * text to identify the component>
     */
    interaction_name?: string;
    /**
     * Used when the interaction is a multi-choice selector on a single question. Examples in
     * GTKY flow.
     */
    option_selected?: string;
    /**
     * The site or application where the order originated
     */
    order_location: CartCreatedOrderLocation;
    package_id?:    string;
    /**
     * Used when the same CTA button is used on several different parts of a single page, but at
     * different sections.
     */
    page_section?:        string;
    process_id?:          string;
    process_name?:        string;
    processing_order_id?: string;
    product_id?:          string;
    products?:            QStartProduct[];
    /**
     * If the track occurs on a screen identify that here.
     */
    screen_name?:           string;
    sku?:                   string;
    target?:                string;
    topline_product_group?: string;
    /**
     * link, button, scroll, hover, input, focus
     */
    track_type?: string;
    type?:       string;
    user_input?: string;
    
}

export interface QStartProduct {
    name?:       string;
    package_id?: string;
    
}

/**
 * At the completion of an answer based questionnaire
 */
export interface QuestionnaireCompleted {
    account_id?: null | string;
    cart_id?:    string;
    /**
     * Where in the LZ ecosystem, Article Center, MyLZ, Overview Page, Q1 flow, etc.
     */
    category?: string;
    cta_copy?: string;
    /**
     * Array of entitlements
     */
    entitlements?: any[];
    graph_type?:   string;
    hash?:         string;
    /**
     * Friendly name of the item being tracked format <ui component> | <event type> | <relevant
     * text to identify the component>
     */
    interaction_name?: string;
    /**
     * Used when the interaction is a multi-choice selector on a single question. Examples in
     * GTKY flow.
     */
    option_selected?: string;
    order_id?:        string;
    /**
     * The site or application where the order originated
     */
    order_location: CartCreatedOrderLocation;
    package_id?:    string;
    /**
     * Used when the same CTA button is used on several different parts of a single page, but at
     * different sections.
     */
    page_section?:        string;
    process_id?:          string;
    process_name?:        string;
    processing_order_id?: string;
    product_id?:          string;
    /**
     * If the track occurs on a screen identify that here.
     */
    screen_name?:           string;
    sku?:                   string;
    target?:                string;
    topline_product_group?: string;
    /**
     * link, button, scroll, hover, input, focus
     */
    track_type?: string;
    type?:       string;
    user_input?: string;
    
}

export interface SegmentConsentPreference {
    category?:    null | string;
    hash?:        null | string;
    screen_name?: null | string;
    
}

export interface SegmentConsentPreferenceUpdated {
    category?:    string;
    hash?:        string;
    screen_name?: string;
    
}

/**
 * Autotracked in some situations (e.g., full page takeovers in myLZ) Records the surface
 * viewed by the visitor and associated properties, used for modals drawers, partial page
 * take overs, etc. If the Url changes = Viewed Page, if not then Viewed Surface
 */
export interface SurfaceViewed {
    /**
     * Also referred to as Workspace ID
     */
    account_id?:         null | string;
    additional_details?: SurfaceViewedAdditionalDetails;
    cart_id?:            string;
    /**
     * Custom categorization of pages based on URL such as Homepage, Overview Page, Login Page,
     * MyLZ, etc.
     */
    category?: string;
    /**
     * Specifically for screens like the activity feed where they want to list all the items
     * present.
     */
    details_list?:        any[];
    entitlements?:        any[];
    graph_type?:          string;
    hash?:                string;
    html_element_id?:     string;
    package_id?:          string;
    page_section?:        string;
    processing_order_id?: string;
    /**
     * Friendly name for easy sorting and filtering
     */
    screen_name?: string;
    /**
     * modal, drawer, partial page takeover
     */
    screen_type?:  string;
    surface_name?: string;
    /**
     * Custom grouping of pages referring to the highest order product or package (LLC, Inc,
     * Nonprofit, DBA) or standalone products (RA, Forms)
     */
    topline_product_group?: null | string;
    type?:                  string;
    
}

export interface SurfaceViewedAdditionalDetails {
    /**
     * calendly widget details
     */
    calendly?:              FluffyCalendly;
    confirm_modal_heading?: string;
    doc_type?:              string;
    document?:              string;
    experiment?:            string;
    experiment_value?:      string;
    industry?:              string;
    partner?:               string;
    report_title?:          string;
    selected?:              string;
    status?:                string;
    surface?:               string;
    takeover?:              string;
    task_name?:             string;
    
}

/**
 * calendly widget details
 */
export interface FluffyCalendly {
    /**
     * unique url for the calendly widget
     */
    form_url?: string;
    
}

/**
 * When a stand alone new trial is started
 */
export interface TrialStarted {
    account_id?:         null | string;
    affiliation?:        string;
    cart_id?:            string;
    category?:           string;
    checkout_id?:        string;
    cogs?:               number;
    conversion_id?:      string;
    coupon?:             string;
    cta_copy?:           string;
    currency?:           string;
    discount_amount?:    string;
    discount_name?:      string;
    entitled?:           string;
    entitlements?:       any[];
    graph_type?:         string;
    hash?:               string;
    installment_amount?: number;
    installment_fee?:    number;
    interaction_name?:   string;
    intercepted_by?:     string;
    ltv?:                number;
    ltv_net?:            number;
    option_selected?:    string;
    order_id?:           string;
    /**
     * The site or application where the order originated
     */
    order_location?:        CartCreatedOrderLocation;
    order_system?:          OrderUpdatedOrderSystem;
    package_id?:            string;
    page_section?:          string;
    parent_order_id?:       string;
    payment_type?:          string;
    process_id?:            string;
    process_name?:          string;
    processing_order_id?:   string;
    products?:              TrialStartedProduct[];
    revenue?:               number;
    screen_name?:           string;
    shipping?:              number;
    subtotal?:              number;
    target?:                string;
    tax?:                   string;
    topline_product_group?: string;
    total?:                 number;
    track_type?:            string;
    user_input?:            string;
    
}

export interface TrialStartedProduct {
    cart_id?:       number;
    category?:      string;
    cogs?:          number;
    graph_type?:    string;
    is_bundle?:     string;
    ltv?:           string;
    name?:          string;
    order_id?:      string;
    order_item_id?: string;
    /**
     * The site or application where the order originated
     */
    order_location?:        ProductOrderLocation;
    package_id?:            string;
    paid_amount?:           string;
    price?:                 number;
    process?:               string;
    process_id?:            string;
    processing_order_id?:   string;
    product_group?:         string;
    product_id?:            string;
    product_line?:          string;
    quantity?:              number;
    sku?:                   string;
    topline_product_group?: string;
    /**
     * Is the trial requiring Opt-In or Opt-out before an actual paid conversion?
     */
    trial_type: TrialType;
    url?:       string;
    
}

/**
 * Is the trial requiring Opt-In or Opt-out before an actual paid conversion?
 */
export type TrialType = "opt-in" | "opt-out";


/**
 * The analytics.js snippet should be available via window.analytics.
 * You can install it by following instructions at: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/
 * Make sure to also include the TypeScript declarations with: `npm install @segment/analytics-next` (install with --save-dev for type definitions only).
 *
 * If you don't want to use the snippet, you can also install the `@segment/analytics-next` library as a *production* dependency and use it like this: 
 * ```ts
 * import { AnalyticsBrowser } from '@segment/analytics-next' 
 * import { setTypewriterOptions } from './analytics'
 *
 * const analytics = AnalyticsBrowser.load({ writeKey: 'SEGMENT_WRITE_KEY' })
 * 
 * setTypewriterOptions({ analytics: analytics })
 */
import type { AnalyticsSnippet, Analytics, AnalyticsBrowser, Options } from '@segment/analytics-next'

declare global {
    interface Window {
        analytics: AnalyticsSnippet;
    }
}

type AnyAnalytics = AnalyticsSnippet | Analytics | AnalyticsBrowser

/** The callback exposed by analytics.js. */
export type Callback = () => void

export type ViolationHandler = (
    message: Record<string, any>,
    violations: any[]
) => void

/**
 * The default handler that is fired if none is supplied with setTypewriterOptions.
 * This handler will log a warning message to the console.
 */
export const defaultValidationErrorHandler: ViolationHandler = (message, violations) => {
    const msg = JSON.stringify(
        {
            type: 'Typewriter JSON Schema Validation Error',
            description:
                `You made an analytics call (${message.event}) using Typewriter that doesn't match the ` +
                'Tracking Plan spec.',
            errors: violations,
        },
        undefined,
        2,
    );

    console.warn(msg);
};


let analytics: () => AnyAnalytics | undefined = () => {
    return window.analytics;
};

/** Options to customize the runtime behavior of a Typewriter client. */
export interface TypewriterOptions {
    /**
     * Underlying analytics instance where analytics calls are forwarded on to.
     * Defaults to window.analytics.
     */
    analytics?: AnyAnalytics;
    /**
     * Handler fired when if an event does not match its spec. This handler
     * does not fire in production mode, because it requires inlining the full
     * JSON Schema spec for each event in your Tracking Plan.
     *
     * By default, it will throw errors if NODE_ENV = "test" so that tests will fail
     * if a message does not match the spec. Otherwise, errors will be logged to stderr.
     */
    onViolation?: ViolationHandler;
}

/**
 * Updates the run-time configuration of this Typewriter client.
 *
 * @param {TypewriterOptions} options - the options to upsert
 *
 * @typedef {Object} TypewriterOptions
 * @property {AnyAnalytics} [analytics] - Underlying analytics instance where analytics
 * 		calls are forwarded on to. Defaults to window.analytics.
 * @property {Function} [onViolation] - Handler fired when if an event does not match its spec. This handler does not fire in
 * 		production mode, because it requires inlining the full JSON Schema spec for each event in your Tracking Plan. By default,
 * 		it will throw errors if NODE_ENV="test" so that tests will fail if a message does not match the spec. Otherwise, errors
 * 		will be logged to stderr.
 */
export function setTypewriterOptions(options: TypewriterOptions) {
    analytics = options.analytics ? () => options.analytics || window.analytics : analytics;
}


/**
 * Helper to attach metadata on Typewriter to outbound requests.
 * This is used for attribution and debugging by the Segment team.
 */
function withTypewriterContext(message: Options = {}): Options {
    return {
        ...message,
        context: {
            ...(message.context || {}),
            typewriter: {
                language: 'typescript',
                version: '9.2.0',
            },
        },
    };
}

/**
 * Fires a 'AlertViewed' track call.
 *
 * @param AlertViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function alertViewed(props: AlertViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Alert Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'AnswersViewed' track call.
 *
 * @param AnswersViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function answersViewed(props: AnswersViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Answers Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'AppScreenViewed' track call.
 *
 * @param AppScreenViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function appScreenViewed(props: AppScreenViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('App Screen Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ApplicationBackgrounded' track call.
 *
 * @param ApplicationBackgrounded props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function applicationBackgrounded(props: ApplicationBackgrounded, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Application Backgrounded', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ApplicationForegrounded' track call.
 *
 * @param ApplicationForegrounded props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function applicationForegrounded(props: ApplicationForegrounded, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Application Foregrounded', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ApplicationInstalled' track call.
 *
 * @param ApplicationInstalled props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function applicationInstalled(props: ApplicationInstalled, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Application Installed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ApplicationOpened' track call.
 *
 * @param ApplicationOpened props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function applicationOpened(props: ApplicationOpened, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Application Opened', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ApplicationUpdated' track call.
 *
 * @param ApplicationUpdated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function applicationUpdated(props: ApplicationUpdated, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Application Updated', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'AsyncContentCreated' track call.
 *
 * @param AsyncContentCreated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function asyncContentCreated(props: AsyncContentCreated, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Async Content Created', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CartCreated' track call.
 *
 * @param CartCreated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function cartCreated(props: CartCreated, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Cart Created', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CartViewed' track call.
 *
 * @param CartViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function cartViewed(props: CartViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Cart Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CheckoutCompleted' track call.
 *
 * @param CheckoutCompleted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function checkoutCompleted(props: CheckoutCompleted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Checkout Completed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CheckoutStarted' track call.
 *
 * @param CheckoutStarted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function checkoutStarted(props: CheckoutStarted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Checkout Started', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CheckoutStepCompleted' track call.
 *
 * @param CheckoutStepCompleted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function checkoutStepCompleted(props: CheckoutStepCompleted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Checkout Step Completed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CheckoutStepViewed' track call.
 *
 * @param CheckoutStepViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function checkoutStepViewed(props: CheckoutStepViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Checkout Step Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ContentViewed' track call.
 *
 * @param ContentViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function contentViewed(props: ContentViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Content Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'CustomerInputValidation' track call.
 *
 * @param CustomerInputValidation props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function customerInputValidation(props: CustomerInputValidation, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Customer Input Validation', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExperimentEnrolled' track call.
 *
 * @param ExperimentEnrolled props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function experimentEnrolled(props: ExperimentEnrolled, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Experiment Enrolled', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExperimentViewed' track call.
 *
 * @param ExperimentViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function experimentViewed(props: ExperimentViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Experiment Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FormDetails' track call.
 *
 * @param FormDetails props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function formDetails(props: FormDetails, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Form Details', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FulfillmentCompleted' track call.
 *
 * @param FulfillmentCompleted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function fulfillmentCompleted(props: FulfillmentCompleted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Fulfillment Completed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FulfillmentStarted' track call.
 *
 * @param FulfillmentStarted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function fulfillmentStarted(props: FulfillmentStarted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Fulfillment Started', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'FulfillmentStatusUpdated' track call.
 *
 * @param FulfillmentStatusUpdated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function fulfillmentStatusUpdated(props: FulfillmentStatusUpdated, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Fulfillment Status Updated', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'Interaction' track call.
 *
 * @param Interaction props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function interaction(props: Interaction, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Interaction', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'LeadGen' track call.
 *
 * @param LeadGen props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function leadGen(props: LeadGen, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Lead Gen', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'OrderCompleted' track call.
 *
 * @param OrderCompleted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function orderCompleted(props: OrderCompleted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Order Completed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'OrderUpdated' track call.
 *
 * @param OrderUpdated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function orderUpdated(props: OrderUpdated, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Order Updated', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PageViewed' track call.
 *
 * @param PageViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function pageViewed(props: PageViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Page Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaymentInfoEntered' track call.
 *
 * @param PaymentInfoEntered props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paymentInfoEntered(props: PaymentInfoEntered, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Payment Info Entered', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ProductAdded' track call.
 *
 * @param ProductAdded props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function productAdded(props: ProductAdded, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Product Added', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ProductClicked' track call.
 *
 * @param ProductClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function productClicked(props: ProductClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Product Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ProductListViewed' track call.
 *
 * @param ProductListViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function productListViewed(props: ProductListViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Product List Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ProductRemoved' track call.
 *
 * @param ProductRemoved props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function productRemoved(props: ProductRemoved, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Product Removed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PromotionClicked' track call.
 *
 * @param PromotionClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function promotionClicked(props: PromotionClicked, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Promotion Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PromotionViewed' track call.
 *
 * @param PromotionViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function promotionViewed(props: PromotionViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Promotion Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PurchaseStepViewed' track call.
 *
 * @param PurchaseStepViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function purchaseStepViewed(props: PurchaseStepViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Purchase Step Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'QStart' track call.
 *
 * @param QStart props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function qStart(props: QStart, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('QStart', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'QuestionnaireCompleted' track call.
 *
 * @param QuestionnaireCompleted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function questionnaireCompleted(props: QuestionnaireCompleted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Questionnaire Completed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'SegmentConsentPreference' track call.
 *
 * @param SegmentConsentPreference props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function segmentConsentPreference(props: SegmentConsentPreference, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Segment Consent Preference', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'SegmentConsentPreferenceUpdated' track call.
 *
 * @param SegmentConsentPreferenceUpdated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function segmentConsentPreferenceUpdated(props: SegmentConsentPreferenceUpdated, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Segment Consent Preference Updated', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'SurfaceViewed' track call.
 *
 * @param SurfaceViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function surfaceViewed(props: SurfaceViewed, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Surface Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'TrialStarted' track call.
 *
 * @param TrialStarted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function trialStarted(props: TrialStarted, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Trial Started', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}

const clientAPI = {
    /**
     * Updates the run-time configuration of this Typewriter client.
     *
     * @param {TypewriterOptions} options - the options to upsert
     *
     * @typedef {Object} TypewriterOptions
     * @property {AnyAnalytics} [analytics] - Underlying analytics instance where analytics
     * 		calls are forwarded on to. Defaults to window.analytics.
     * @property {Function} [onViolation] - Handler fired when if an event does not match its spec. This handler does not fire in
     * 		production mode, because it requires inlining the full JSON Schema spec for each event in your Tracking Plan. By default,
     * 		it will throw errors if NODE_ENV="test" so that tests will fail if a message does not match the spec. Otherwise, errors
     * 		will be logged to stderr.
     */
    setTypewriterOptions,

    /**
     * Fires a 'AlertViewed' track call.
     *
     * @param AlertViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    alertViewed,
    /**
     * Fires a 'AnswersViewed' track call.
     *
     * @param AnswersViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    answersViewed,
    /**
     * Fires a 'AppScreenViewed' track call.
     *
     * @param AppScreenViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    appScreenViewed,
    /**
     * Fires a 'ApplicationBackgrounded' track call.
     *
     * @param ApplicationBackgrounded props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    applicationBackgrounded,
    /**
     * Fires a 'ApplicationForegrounded' track call.
     *
     * @param ApplicationForegrounded props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    applicationForegrounded,
    /**
     * Fires a 'ApplicationInstalled' track call.
     *
     * @param ApplicationInstalled props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    applicationInstalled,
    /**
     * Fires a 'ApplicationOpened' track call.
     *
     * @param ApplicationOpened props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    applicationOpened,
    /**
     * Fires a 'ApplicationUpdated' track call.
     *
     * @param ApplicationUpdated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    applicationUpdated,
    /**
     * Fires a 'AsyncContentCreated' track call.
     *
     * @param AsyncContentCreated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    asyncContentCreated,
    /**
     * Fires a 'CartCreated' track call.
     *
     * @param CartCreated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    cartCreated,
    /**
     * Fires a 'CartViewed' track call.
     *
     * @param CartViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    cartViewed,
    /**
     * Fires a 'CheckoutCompleted' track call.
     *
     * @param CheckoutCompleted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    checkoutCompleted,
    /**
     * Fires a 'CheckoutStarted' track call.
     *
     * @param CheckoutStarted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    checkoutStarted,
    /**
     * Fires a 'CheckoutStepCompleted' track call.
     *
     * @param CheckoutStepCompleted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    checkoutStepCompleted,
    /**
     * Fires a 'CheckoutStepViewed' track call.
     *
     * @param CheckoutStepViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    checkoutStepViewed,
    /**
     * Fires a 'ContentViewed' track call.
     *
     * @param ContentViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    contentViewed,
    /**
     * Fires a 'CustomerInputValidation' track call.
     *
     * @param CustomerInputValidation props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    customerInputValidation,
    /**
     * Fires a 'ExperimentEnrolled' track call.
     *
     * @param ExperimentEnrolled props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    experimentEnrolled,
    /**
     * Fires a 'ExperimentViewed' track call.
     *
     * @param ExperimentViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    experimentViewed,
    /**
     * Fires a 'FormDetails' track call.
     *
     * @param FormDetails props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    formDetails,
    /**
     * Fires a 'FulfillmentCompleted' track call.
     *
     * @param FulfillmentCompleted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    fulfillmentCompleted,
    /**
     * Fires a 'FulfillmentStarted' track call.
     *
     * @param FulfillmentStarted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    fulfillmentStarted,
    /**
     * Fires a 'FulfillmentStatusUpdated' track call.
     *
     * @param FulfillmentStatusUpdated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    fulfillmentStatusUpdated,
    /**
     * Fires a 'Interaction' track call.
     *
     * @param Interaction props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    interaction,
    /**
     * Fires a 'LeadGen' track call.
     *
     * @param LeadGen props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    leadGen,
    /**
     * Fires a 'OrderCompleted' track call.
     *
     * @param OrderCompleted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    orderCompleted,
    /**
     * Fires a 'OrderUpdated' track call.
     *
     * @param OrderUpdated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    orderUpdated,
    /**
     * Fires a 'PageViewed' track call.
     *
     * @param PageViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    pageViewed,
    /**
     * Fires a 'PaymentInfoEntered' track call.
     *
     * @param PaymentInfoEntered props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paymentInfoEntered,
    /**
     * Fires a 'ProductAdded' track call.
     *
     * @param ProductAdded props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    productAdded,
    /**
     * Fires a 'ProductClicked' track call.
     *
     * @param ProductClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    productClicked,
    /**
     * Fires a 'ProductListViewed' track call.
     *
     * @param ProductListViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    productListViewed,
    /**
     * Fires a 'ProductRemoved' track call.
     *
     * @param ProductRemoved props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    productRemoved,
    /**
     * Fires a 'PromotionClicked' track call.
     *
     * @param PromotionClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    promotionClicked,
    /**
     * Fires a 'PromotionViewed' track call.
     *
     * @param PromotionViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    promotionViewed,
    /**
     * Fires a 'PurchaseStepViewed' track call.
     *
     * @param PurchaseStepViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    purchaseStepViewed,
    /**
     * Fires a 'QStart' track call.
     *
     * @param QStart props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    qStart,
    /**
     * Fires a 'QuestionnaireCompleted' track call.
     *
     * @param QuestionnaireCompleted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    questionnaireCompleted,
    /**
     * Fires a 'SegmentConsentPreference' track call.
     *
     * @param SegmentConsentPreference props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    segmentConsentPreference,
    /**
     * Fires a 'SegmentConsentPreferenceUpdated' track call.
     *
     * @param SegmentConsentPreferenceUpdated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    segmentConsentPreferenceUpdated,
    /**
     * Fires a 'SurfaceViewed' track call.
     *
     * @param SurfaceViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    surfaceViewed,
    /**
     * Fires a 'TrialStarted' track call.
     *
     * @param TrialStarted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    trialStarted,
};

export default /* @__PURE__ */ new Proxy<typeof clientAPI>(clientAPI, {
    get(target, method) {
        if (typeof method === 'string' && target.hasOwnProperty(method)) {
            return target[method as keyof typeof clientAPI];
        }

        return () => {
            console.warn(`⚠️  You made an analytics call (${String(method)}) that can't be found. Either:
         a) Re-generate your typewriter client: \`npx typewriter\`
         b) Add it to your Tracking Plan: https://app.segment.com/segment-oscb/protocols/tracking-plans/rs_1zTHJU9fd5mt7cndWnd4PgJbMCE`);
            const a = analytics();
            if (a) {
                a.track(
                    'Unknown Analytics Call Fired',
                    {
                        method,
                    },
                    withTypewriterContext(),
                );
            }
        };
    },
});

